import React, { useEffect, useState } from 'react';
import Compressor from 'compressorjs';
import { XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline';

import Modal from '../components/Shell/Modal';
import Button from '../components/Button';
import BetaAttributes from '../components/Attributes';
import {
	AttributeRow,
	TemplateField,
	TemplateTextarea,
	TemplateDropdown,
} from '../components/Attributes/Attributes_old';
import { SelectAssets } from '../components/SelectAsset';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import useRequired from '../hooks/useRequired';

import { addTicket } from '../api';
import { getModules } from '../api/modules';

const MODAL_NAME = 'createTicket';

const CreateTicketModal = () => {
	const [form, setForm] = useState({});
	const [fileList, setFileList] = useState([]);
	const [isUploading, setIsUploading] = useState(false);
	const [selectedAssets, setSelectedAssets] = useState([]);

	const { open: options, setClose } = useModal(MODAL_NAME);

	const { execute: onAddTicket, status: isAddingTicket } = useAsync(addTicket, {
		immediate: false,
	});

	const { value: modules, execute: onRefreshModules } = useAsync(getModules, {
		immediate: false,
	});

	// Find tickets module for category selection
	const ticketsModule = modules?.rows?.find((m) => m.key === 'tickets');

	useEffect(() => {
		if (options) {
			onRefreshModules();
			setForm({
				severity: 'minor',
			});
			setFileList([]);
			setSelectedAssets([]);
		} else {
			setForm({});
			setFileList([]);
			setSelectedAssets([]);
		}
	}, [options]);

	const setItem = (key, value) => setForm({ ...form, [key]: value });

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		setIsUploading(true);

		// Check if it's an image that can be compressed
		if (file.type.startsWith('image/')) {
			new Compressor(file, {
				quality: 0.6,
				maxWidth: 1920,
				maxHeight: 1920,
				convertSize: 500000, // Convert to JPEG if over 500KB
				success: (compressedBlob) => {
					// Create a File object from the compressed Blob
					const compressedFile = new File(
						[compressedBlob],
						file.name,
						{ type: compressedBlob.type }
					);

					// Create thumbnail preview
					const reader = new FileReader();
					reader.onloadend = () => {
						setIsUploading(false);
						setFileList((prev) => [
							...prev,
							{
								name: file.name,
								original: compressedFile,
								size: compressedFile.size,
								thumbUrl: reader.result,
								type: 'image',
							},
						]);
					};
					reader.readAsDataURL(compressedBlob);
				},
				error: () => {
					// If compression fails, use original file
					setIsUploading(false);
					setFileList((prev) => [
						...prev,
						{
							name: file.name,
							original: file,
							size: file.size,
							type: 'image',
						},
					]);
				},
			});
		} else {
			// For non-image files (PDFs, docs, etc.)
			setIsUploading(false);
			setFileList((prev) => [
				...prev,
				{
					name: file.name,
					original: file,
					size: file.size,
					type: 'document',
				},
			]);
		}

		// Reset the input
		e.target.value = '';
	};

	const removeFile = (index) => {
		setFileList(fileList.filter((_, i) => i !== index));
	};

	const configurePayload = () => {
		return {
			information: {
				type: 'general',
				...(form?.category?.value ? { categoryId: form.category.value } : {}),
				...(form?.subject ? { summary: form.subject } : {}),
				...(form?.description ? { description: form.description } : {}),
				...(form?.severity ? { severity: form.severity } : {}),
				...(selectedAssets?.length ? { assets: selectedAssets } : {}),
			},
			files: fileList?.map(({ original }) => original),
			bucket: 'tickets',
		};
	};

	const hasAllRequiredValues = useRequired(form, ['subject']);

	const handleSubmit = () => {
		onAddTicket(configurePayload(), () => {
			if (typeof options?.onSuccess === 'function') {
				options.onSuccess();
			}
			setClose();
		});
	};

	return (
		<Modal
			title="Create Ticket"
			open={!!options}
			onClose={() => setClose()}
			size="xl"
		>
			<div className="space-y-5 text-left mt-4">
				{/* Category Selection */}
				<div>
					<BetaAttributes
						field={{
							label: 'Category',
							type: 'category',
							key: 'category',
							moduleId: ticketsModule?.id,
							options: { module_key: 'tickets' },
						}}
						form={form}
						onChange={(index, value) => {
							setForm({ ...form, category: { value } });
						}}
					/>
				</div>

				{/* Subject */}
				<TemplateField
					name="Subject"
					placeholder="Brief summary of the issue"
					dataIndex="subject"
					value={form?.subject}
					onChange={(key, value) => setItem(key, value)}
					required
					maxLength={100}
					showMaxLength
				/>

				{/* Description */}
				<TemplateTextarea
					name="Description"
					placeholder="Provide additional details about the issue..."
					dataIndex="description"
					onChange={(key, value) => setItem(key, value)}
					value={form?.description}
				/>

				{/* Severity */}
				<TemplateDropdown
					name="Severity"
					options={[
						{ label: 'Critical', value: 'critical' },
						{ label: 'Major', value: 'major' },
						{ label: 'Minor', value: 'minor' },
					]}
					placeholder="Select severity level"
					dataIndex="severity"
					onChange={(key, value) => setItem(key, value)}
					value={form?.severity}
				/>

				{/* Related Assets */}
				<AttributeRow name="Related Assets" optional>
					<SelectAssets
						value={selectedAssets}
						onChange={(assetIds) => setSelectedAssets(assetIds)}
						placeholder="Search and select assets..."
					/>
				</AttributeRow>

				{/* Document Upload */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Attachments
					</label>
					<div className="border-2 border-dashed border-gray-300 rounded-lg px-6 py-4">
						<div className="text-center">
							<DocumentIcon className="mx-auto h-10 w-10 text-gray-400" />
							<div className="mt-2 text-sm text-gray-600">
								<label
									htmlFor="ticket-file-upload"
									className="relative cursor-pointer rounded-md bg-white font-medium text-indigo-600 focus-within:outline-none hover:text-indigo-500"
								>
									<span>Upload files</span>
									<input
										id="ticket-file-upload"
										name="ticket-file-upload"
										type="file"
										className="sr-only"
										accept="image/jpeg, image/png, application/pdf, .doc, .docx, .xls, .xlsx"
										onChange={handleFileUpload}
									/>
								</label>
								<span className="pl-1">or drag and drop</span>
							</div>
							<p className="text-xs text-gray-500 mt-1">
								PNG, JPG, PDF, DOC up to 10MB
							</p>
						</div>
					</div>

					{/* File List */}
					{fileList?.length > 0 && (
						<ul className="mt-4 space-y-2">
							{fileList.map((file, index) => (
								<li
									key={`file-${index}`}
									className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
								>
									<div className="flex items-center min-w-0">
										{file.type === 'image' && file.thumbUrl ? (
											<img
												src={file.thumbUrl}
												alt={file.name}
												className="h-10 w-10 object-cover rounded mr-3"
											/>
										) : (
											<DocumentIcon className="h-10 w-10 text-gray-400 mr-3" />
										)}
										<div className="min-w-0">
											<p className="text-sm font-medium text-gray-900 truncate">
												{file.name}
											</p>
											<p className="text-xs text-gray-500">
												{`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
											</p>
										</div>
									</div>
									<button
										type="button"
										onClick={() => removeFile(index)}
										className="ml-4 flex-shrink-0"
									>
										<XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
									</button>
								</li>
							))}
						</ul>
					)}

					{/* Upload spinner */}
					{isUploading && (
						<div className="flex items-center justify-center mt-3">
							<svg
								className="animate-spin h-5 w-5 text-indigo-600"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
								/>
								<path
									className="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
							<span className="ml-2 text-sm text-gray-500">Uploading...</span>
						</div>
					)}
				</div>
			</div>

			{/* Footer */}
			<div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
				<Button type="secondary" onClick={() => setClose()} className="mr-3">
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={!hasAllRequiredValues || isAddingTicket === 'pending'}
				>
					{isAddingTicket === 'pending'
						? fileList?.length
							? 'Uploading...'
							: 'Creating...'
						: 'Create Ticket'}
				</Button>
			</div>
		</Modal>
	);
};

export default CreateTicketModal;
